import { motion } from "framer-motion";
import { MaskLines, EASE, PartialCircle } from "./Reveal-graphics";
import { scrollToId } from "@/hooks/useLenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1782974501161-d12078129d38?q=80&w=1400&auto=format&fit=crop";

export default function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
      <PartialCircle className="absolute -bottom-24 -left-24 w-72 md:w-96 text-brown opacity-30 pointer-events-none" />

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full max-w-[1600px] mx-auto px-6 md:px-10 pt-32 pb-24 lg:pt-24 lg:pb-0">
        <div className="lg:col-span-7 order-2 lg:order-1 relative">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
            className="flex items-center gap-4 mb-9"
          >
            <span className="h-px w-10 bg-brown" />
            <p className="text-[11px] uppercase tracking-[0.32em] font-medium text-smoke" data-testid="hero-eyebrow">
              Anichya Coffee
            </p>
          </motion.div>

          <h1 className="font-serif text-ink text-[14vw] sm:text-[10.5vw] lg:text-[5.6rem] xl:text-[6.6rem] leading-[1.05] tracking-[-0.005em] font-normal">
            <MaskLines
              onMount
              delay={0.45}
              lines={[
                "Coffee, in its",
                <em key="m" className="italic text-forest">moment.</em>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-smoke"
            data-testid="hero-supporting-text"
          >
            Single-origin coffees from India, selected in small lots and released while they
            are at their best.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
            className="mt-12"
          >
            <button
              onClick={() => scrollToId("story")}
              className="rounded-full bg-forest text-paper text-xs uppercase tracking-[0.22em] px-9 py-4 hover:bg-brown transition-colors duration-300"
              data-testid="hero-explore-btn"
            >
              Explore
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <motion.div
            initial={{ clipPath: "inset(10% 6% 10% 6%)", opacity: 0.5 }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
            className="relative overflow-hidden h-[46vh] sm:h-[54vh] lg:h-[84vh]"
            data-testid="hero-image"
          >
            <img
              src={HERO_IMG}
              alt="Ripe red coffee cherries on the branch"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        onClick={() => scrollToId("story")}
        className="absolute bottom-6 left-6 lg:bottom-9 lg:left-10 flex flex-col items-start gap-3 group cursor-pointer"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to the next section"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-smoke group-hover:text-brown transition-colors duration-300">
          Scroll
        </span>
        <span className="relative block h-14 w-px bg-brown/30">
          <span className="absolute left-1/2 top-0 h-full -translate-x-1/2 flex justify-center">
            <motion.span
              className="block h-1.5 w-1.5 rounded-full bg-brown"
              animate={{ y: [0, 48], opacity: [0, 1, 1, 0] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </span>
        </span>
      </motion.button>
    </section>
  );
}
